import React, { useEffect, useRef, useState } from "react";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToWindowEdges, } from "@dnd-kit/modifiers"
import { arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import { Column } from "./sortable-task";
import { Task } from "./dragable.component";
import "./drag-drop.css";
import { IGlimpseDragDrop } from "interfaces/DragDrop.interface";

import { IMyCareersPrioritize } from "interfaces/OnbaordingInterface";
import { CareerCategory } from "enums/glimps.enums";

const DragAndDrop: React.FC<IGlimpseDragDrop> = ({
  data,
  displayUnsorted,
  onboardingSorted,
  showUnsortedError,
  onDropped,
}) => {
  const [render, setRender] = useState<number>(0);
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 10,
      distance: 10,
      tolerance: 15,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 10,
      distance: 10,
      tolerance: 15,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);
  // const sensors = useSensors(
  //   useSensor(MouseSensor, {
  //     activationConstraint: {
  //       distance: 8,
  //     },
  //   }),
  //   useSensor(TouchSensor, {
  //     activationConstraint: {
  //       distance: 8,
  //     },
  //   })
  // );

  const [columns, setColumns] = useState([
    {
      category: CareerCategory.unsorted,
      title: "Unsorted",
      subtitle: "You have sorted all of the careers.",
    },
    {
      category: CareerCategory.top_three,
      title: "Top 3",
      subtitle: "Highest potential for my future",
    },
    {
      category: CareerCategory.watch_list,
      title: "Watch List",
      subtitle: "Have some potential for my future",
    },
    {
      category: CareerCategory.discard,
      title: "Discard",
      subtitle: "Not in my future",
    },
  ]);

  const [tasks, setTasks] = useState<IMyCareersPrioritize[]>(data);

  const [activeTask, setActiveTask] = useState(null);
  const bumpedRef = useRef<string | null>(null);
  const isDraggingRef = useRef<boolean>(false);

  // Sync local tasks with refreshed server data after refetch.
  // Skip while a drag is in flight so we don't yank items mid-drop.
  useEffect(() => {
    if (isDraggingRef.current) return;
    setTasks(data);
  }, [data]);

  const onDragStart = (event: any) => {
    bumpedRef.current = null;
    isDraggingRef.current = true;
    if (event.active.data.current?.type === "Task") {
      setActiveTask(event.active.data.current.task);
      return;
    }
  };
  const onDragOver = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    if (activeId === overId) return;
    const isActiveATask = active.data.current?.type === "Task";
    const isOverATask = over.data.current?.type === "Task";
    if (!isActiveATask) return;

    // Dropping a task over another task
    if (isActiveATask && isOverATask) {
      setTasks((task) => {
        const activeIndex = task.findIndex((t) => t.id === activeId);
        const overIndex = task.findIndex((t) => t.id === overId);
        if (task[activeIndex].category !== task[overIndex].category) {
          const targetCategory = task[overIndex].category;
          // Enforce Top 3 limit: bump the bottom item to Watch List
          if (targetCategory === CareerCategory.top_three) {
            const topThreeOthers = task.filter(
              (t) => t.category === CareerCategory.top_three && t.id !== activeId
            );
            if (topThreeOthers.length >= 3) {
              const lastItem = topThreeOthers[topThreeOthers.length - 1];
              const lastIdx = task.findIndex((t) => t.id === lastItem.id);
              task[lastIdx].category = CareerCategory.watch_list;
              bumpedRef.current = lastItem.id;
            }
          }
          task[activeIndex].category = targetCategory;
        }
        return arrayMove(task, activeIndex, overIndex);
      });
    }
    // Dropping a task over an empty column
    const isOverAColumn = over.data.current?.type === "Column";
    if (isActiveATask && isOverAColumn) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        // Enforce Top 3 limit: bump the bottom item to Watch List
        if (overId === CareerCategory.top_three) {
          const topThreeOthers = tasks.filter(
            (t) => t.category === CareerCategory.top_three && t.id !== activeId
          );
          if (topThreeOthers.length >= 3) {
            const lastItem = topThreeOthers[topThreeOthers.length - 1];
            const lastIdx = tasks.findIndex((t) => t.id === lastItem.id);
            tasks[lastIdx].category = CareerCategory.watch_list;
            bumpedRef.current = lastItem.id;
          }
        }
        tasks[activeIndex].category = overId;
        return arrayMove(tasks, activeIndex, activeIndex);
      });
    }
  };
  const dragEnd = async (event: DragEndEvent) => {
    setActiveTask(null);
    isDraggingRef.current = false;
    // If an item was bumped from Top 3 to Watch List, persist it FIRST.
    // This reduces the top_three count so the BE validator allows the
    // dragged item into top_three, and avoids deadlocks from concurrent
    // mutations on the same user's rows.
    if (bumpedRef.current) {
      const bumpedTask = tasks.find((t) => t.id === bumpedRef.current);
      if (bumpedTask) {
        try {
          await onDropped(bumpedTask, tasks);
        } catch {
          // Bump failed — proceed with the drop anyway; the user can retry.
        }
      }
      bumpedRef.current = null;
    }
    onDropped(event.active.data.current?.task as IMyCareersPrioritize, tasks);
  };
  const unsortedLength = tasks.filter(
    (task) => task?.category === CareerCategory.unsorted
  ).length;

  const unsortedCol = columns.find((c) => c.category === CareerCategory.unsorted)!;
  const sortedCols = columns.filter((c) => c.category !== CareerCategory.unsorted);
  const topThreeCount = tasks.filter((t) => t.category === CareerCategory.top_three).length;

  return (
    <div
      className={`flex flex-col w-full p-0 ${!onboardingSorted && "sm:p-4"} m-auto select-none`}
    >
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={dragEnd}
        modifiers={[restrictToWindowEdges]}
      >
        {onboardingSorted ? (
          <div className="grid grid-cols-1 gap-4">
            {unsortedLength > 0 && (
              <div className="bg-background">
                <Column
                  column={unsortedCol}
                  tasks={tasks.filter((t) => t.category === CareerCategory.unsorted)}
                  totalTopThree={topThreeCount}
                  onboardingSorted={onboardingSorted}
                  showUnsortedError={showUnsortedError}
                />
              </div>
            )}
            {sortedCols.map((col) => (
              <div key={col.category} className="bg-white min-h-[100px]">
                <Column
                  column={col}
                  tasks={tasks.filter((t) => t.category === col.category)}
                  totalTopThree={topThreeCount}
                  onboardingSorted={onboardingSorted}
                  showUnsortedError={showUnsortedError}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Row 1: Unsorted */}
            {unsortedLength > 0 && (
              <div className="fixed md:relative bottom-0 w-full md:w-auto z-10">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h3 className="text-display-lg text-brand">Unsorted</h3>
                  {showUnsortedError && (
                    <span className="text-body-sm text-destructive">
                      {unsortedLength === 1
                        ? "1 career still needs to be sorted."
                        : `${unsortedLength} careers still need to be sorted.`}
                    </span>
                  )}
                </div>
                <Column
                  column={{...unsortedCol, title: ""}}
                  tasks={tasks.filter((t) => t.category === CareerCategory.unsorted)}
                  totalTopThree={topThreeCount}
                  onboardingSorted={onboardingSorted}
                  showUnsortedError={showUnsortedError}
                />
              </div>
            )}

            {/* Row 2: Sorted heading */}
            <h3 className="text-display-lg text-brand">Sorted</h3>

            {/* Row 3: Top 3, Watch List, Discard */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {sortedCols.map((col) => (
                <div key={col.category} className="rounded-lg shadow-card">
                  <Column
                    column={col}
                    tasks={tasks.filter((t) => t.category === col.category)}
                    totalTopThree={topThreeCount}
                    onboardingSorted={onboardingSorted}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {createPortal(
          <DragOverlay>
            {activeTask && (
              <Task
                task={activeTask}
                index={0}
                totalTopThree={0}
                onboardingSorted={onboardingSorted}
              />
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );
};

export default DragAndDrop;
